/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { RefreshCw, ChevronUp, ChevronDown, FileText, Loader } from 'lucide-react';
import type { AcceptanceReportSummary } from '@shared/types/acceptance-reports';
import { EpicAccordion } from './EpicAccordion';

interface AcceptanceReportSectionProps {
  report: AcceptanceReportSummary | null;
  isGenerating: boolean;
  onRefresh: () => void;
  onStoryClick?: (storyId: string, epicId: string) => void;
}

const STATUS_COLORS = {
  accepted: '#10b981',
  readyForReview: '#f59e0b',
  scoped: '#8b5cf6',
  required: '#6b7280',
};

export function AcceptanceReportSection({
  report,
  isGenerating,
  onRefresh,
  onStoryClick,
}: AcceptanceReportSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!report && !isGenerating) {
    return null;
  }

  const toggleCollapsed = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCollapsed(!isCollapsed);
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRefresh();
  };

  const completionPercentage = report && report.totals.total > 0
    ? Math.round((report.totals.accepted / report.totals.total) * 100)
    : 0;

  return (
    <div
      style={{
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: '1px solid #e0e0e0',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: isCollapsed ? 0 : '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FileText size={18} style={{ color: '#7c3aed' }} />
          <h5 style={{ margin: 0, color: '#333', fontSize: '15px', fontWeight: 600 }}>
            Acceptance Report
          </h5>
          {report && (
            <span
              style={{
                fontSize: '12px',
                color: '#6b7280',
                backgroundColor: '#f3f4f6',
                padding: '2px 8px',
                borderRadius: '10px',
              }}
            >
              {report.totals.total} criteria
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            onClick={handleRefresh}
            disabled={isGenerating}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#fff',
              color: '#374151',
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              opacity: isGenerating ? 0.7 : 1,
            }}
          >
            {isGenerating ? (
              <>
                <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                Generating...
              </>
            ) : (
              <>
                <RefreshCw size={14} />
                Refresh
              </>
            )}
          </button>

          <button
            onClick={toggleCollapsed}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#fff',
              color: '#374151',
              cursor: 'pointer',
            }}
          >
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <>
          {isGenerating && !report ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px',
                color: '#6b7280',
              }}
            >
              <Loader size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '12px' }} />
              Generating acceptance report...
            </div>
          ) : report ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  marginBottom: '16px',
                }}
              >
                <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: STATUS_COLORS.accepted,
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#374151' }}>
                      <strong style={{ color: STATUS_COLORS.accepted }}>{report.totals.accepted}</strong> Accepted
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: STATUS_COLORS.readyForReview,
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#374151' }}>
                      <strong style={{ color: STATUS_COLORS.readyForReview }}>{report.totals.readyForReview}</strong> Ready For Review
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: STATUS_COLORS.scoped,
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#374151' }}>
                      <strong style={{ color: STATUS_COLORS.scoped }}>{report.totals.scoped}</strong> Scoped
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: STATUS_COLORS.required,
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#374151' }}>
                      <strong style={{ color: STATUS_COLORS.required }}>{report.totals.required}</strong> Required
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <div
                    style={{
                      width: '100px',
                      height: '8px',
                      backgroundColor: '#e5e7eb',
                      borderRadius: '4px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${completionPercentage}%`,
                        height: '100%',
                        backgroundColor: STATUS_COLORS.accepted,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: completionPercentage === 100 ? STATUS_COLORS.accepted : '#374151',
                    }}
                  >
                    {completionPercentage}%
                  </span>
                </div>
              </div>

              {report.epicSummaries.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '24px',
                    color: '#6b7280',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                  }}
                >
                  <p style={{ margin: 0 }}>
                    No acceptance criteria found for this release.
                  </p>
                  <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#9ca3af' }}>
                    Tag acceptance criteria with this release ID to see them here.
                  </p>
                </div>
              ) : (
                <div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '24px 1fr repeat(4, 60px) 60px',
                      padding: '8px 12px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '6px 6px 0 0',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '8px',
                    }}
                  >
                    <div></div>
                    <div>Epic</div>
                    <div style={{ textAlign: 'center', color: STATUS_COLORS.accepted }}>Acc</div>
                    <div style={{ textAlign: 'center', color: STATUS_COLORS.readyForReview }}>Rev</div>
                    <div style={{ textAlign: 'center', color: STATUS_COLORS.scoped }}>Scp</div>
                    <div style={{ textAlign: 'center', color: STATUS_COLORS.required }}>Req</div>
                    <div style={{ textAlign: 'center' }}>Done</div>
                  </div>

                  {report.epicSummaries.map((epic) => (
                    <EpicAccordion
                      key={epic.epicId}
                      epic={epic}
                      onStoryClick={onStoryClick}
                    />
                  ))}
                </div>
              )}

              <div
                style={{
                  marginTop: '12px',
                  fontSize: '11px',
                  color: '#9ca3af',
                  textAlign: 'right',
                }}
              >
                Generated: {new Date(report.generatedAt).toLocaleString()}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
